import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import MultiSelect from '../ui/MultiSelect'
import InlineSelect from '../ui/InlineSelect'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useCatalog } from '../../contexts/CatalogContext'
import {
  expandPassSequenceToLabels,
  passSectionInputForActiveBevel
} from '../../utils/passSequenceExpanded'

export default function WpsClampDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  // Slice 09 of pqr-preview-field-navigation. When the PQR canvas
  // routes a Clamp-class note row (Welding Power Supplies / Firmware /
  // Program / Clamp Type / Clamp Release) to this page, it appends
  // `?focusField=<key>&returnTo=…`. We resolve the marker via
  // `[data-focus-field="<key>"]`, scroll-into-view + flash, and try
  // to focus an inner control. `returnTo` rewires the page header's
  // Back button so the user lands back on the PQR preview.
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const formContainerRef = useRef<HTMLDivElement>(null)
  useFieldFocusHandler({
    ready: true,
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const root = formContainerRef.current ?? document
      const el = root.querySelector(
        `[data-focus-field="${params.focusField}"]`
      ) as HTMLElement | null
      if (!el) return null
      const inner = el.querySelector(
        'input, select, textarea, button, [role="button"]'
      ) as HTMLElement | null
      return inner ?? el
    }
  })
  const { getProject } = useProjects()
  const { getSection, updateSection } = useWpsSections()
  const { getLabelsMerged, addLabel } = useCatalog()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [clampType, setClampType] = useState<string[]>([])
  const [clampCount, setClampCount] = useState('2')
  const [removalNote, setRemovalNote] = useState('')
  const [passPowerSupplies, setPassPowerSupplies] = useState<Record<string, string>>({})
  const [storedPowerSupplyExtras, setStoredPowerSupplyExtras] = useState<string[]>([])
  const [powerSupplyDetails, setPowerSupplyDetails] = useState<
    Record<
      string,
      {
        enabled: boolean
        firmwareVersions: string[]
        programs: string[]
        firmwareOptions: string[]
        programOptions: string[]
      }
    >
  >({})
  const [froniusProgram, setFroniusProgram] = useState<{ hotPass: string; fillAndCaps: string }>({
    hotPass: '',
    fillAndCaps: ''
  })

  const [savedClampJson, setSavedClampJson] = useState('')
  const [saving, setSaving] = useState(false)
  const pendingClampSnapshot = useRef(false)

  useEffect(() => {
    setSavedClampJson('')
  }, [pipeId])

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('clamp')
    if (parsed) {
      try {
        setClampType(parsed.clampType || [])
        setClampCount(parsed.clampCount || '2')
        setRemovalNote(parsed.removalNote || '')
        const storedOptions = Array.isArray(parsed.powerSupplyOptions)
          ? parsed.powerSupplyOptions.map((s: unknown) => String(s))
          : []
        setStoredPowerSupplyExtras(storedOptions)
        setPassPowerSupplies(parsed.passPowerSupplies || {})
        if (parsed.powerSupplyDetails && typeof parsed.powerSupplyDetails === 'object') {
          setPowerSupplyDetails(parsed.powerSupplyDetails)
        } else if (parsed.firmwareVersions || parsed.programs) {
          const combinedSupplies = Array.from(
            new Set(Object.values(parsed.passPowerSupplies || {}).filter(Boolean))
          ) as string[]
          const fallbackDetails = combinedSupplies.reduce(
            (acc: Record<string, any>, supply) => {
              acc[supply] = {
                enabled: true,
                firmwareVersions: parsed.firmwareVersions || [],
                programs: parsed.programs || [],
                firmwareOptions: parsed.firmwareOptions || [],
                programOptions: parsed.programOptions || []
              }
              return acc
            },
            {} as Record<string, {
              enabled: boolean
              firmwareVersions: string[]
              programs: string[]
              firmwareOptions: string[]
              programOptions: string[]
            }>
          )
          setPowerSupplyDetails(fallbackDetails)
        }
        const fp = parsed.froniusProgram
        if (fp && typeof fp === 'object') {
          setFroniusProgram({
            hotPass: String((fp as { hotPass?: unknown }).hotPass ?? ''),
            fillAndCaps: String((fp as { fillAndCaps?: unknown }).fillAndCaps ?? '')
          })
        } else {
          setFroniusProgram({ hotPass: '', fillAndCaps: '' })
        }
        pendingClampSnapshot.current = true
      } catch (error) {
        // ignore
      }
    } else {
      pendingClampSnapshot.current = true
    }
  }, [pipeId, getSection])

  const clampOptions = useMemo(
    () => getLabelsMerged('equipment_clamp_type', clampType),
    [getLabelsMerged, clampType]
  )

  const powerSupplyOptions = useMemo(
    () =>
      getLabelsMerged('equipment_power_supply', [
        ...storedPowerSupplyExtras,
        ...Object.values(passPowerSupplies).filter((v): v is string => Boolean(v))
      ]),
    [getLabelsMerged, storedPowerSupplyExtras, passPowerSupplies]
  )

  const clampSaveBody = useMemo(
    () => ({
      clampType,
      clampCount,
      removalNote,
      storedPowerSupplyExtras,
      powerSupplyOptions,
      passPowerSupplies,
      powerSupplyDetails,
      froniusProgram
    }),
    [
      clampType,
      clampCount,
      removalNote,
      storedPowerSupplyExtras,
      powerSupplyOptions,
      passPowerSupplies,
      powerSupplyDetails,
      froniusProgram
    ]
  )

  const isDirty = useMemo(() => {
    if (!savedClampJson) return false
    return JSON.stringify(clampSaveBody) !== savedClampJson
  }, [clampSaveBody, savedClampJson])

  useLayoutEffect(() => {
    if (!pendingClampSnapshot.current) return
    pendingClampSnapshot.current = false
    setSavedClampJson(JSON.stringify(clampSaveBody))
  }, [clampSaveBody])

  // Pass Details stores sequence per bevel (`bevelPasses`). Legacy pipe sets
  // still use top-level `passSequence`. Without resolving the active bevel,
  // `passList` stays empty and Power supply / Firmware / Program never render.
  const passList = React.useMemo(() => {
    if (!pipeId) return [] as string[]
    const parsed = getSection('pass')
    if (!parsed) return []
    try {
      const input = passSectionInputForActiveBevel(parsed)
      if (!input) return []
      return expandPassSequenceToLabels(input).filter(
        (label) => label !== 'Root Misfire' && !/^Root Misfire \d+$/.test(label)
      )
    } catch {
      return []
    }
  }, [pipeId, getSection])

  // Migrate legacy type keys (Fill) onto first expanded label (Fill 1) once.
  useEffect(() => {
    if (passList.length === 0) return
    setPassPowerSupplies((prev) => {
      let changed = false
      const next = { ...prev }
      const migrate = (from: string, to: string) => {
        if (next[from] && !next[to]) {
          next[to] = next[from]
          delete next[from]
          changed = true
        }
      }
      migrate('Fill', 'Fill 1')
      migrate('Cap', 'Cap 1')
      migrate('Strip', 'Strip 1')
      migrate('Root Misfire', 'Root')
      return changed ? next : prev
    })
  }, [passList])

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    const body = {
      clampType,
      clampCount,
      removalNote,
      powerSupplyOptions,
      passPowerSupplies,
      powerSupplyDetails,
      froniusProgram
    }
    setSaving(true)
    try {
      await updateSection('clamp', body)
      setSavedClampJson(JSON.stringify(body))
      toast.success('Equipment details saved')
      navigate(-1)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save equipment details')
    } finally {
      setSaving(false)
    }
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Equipment Details not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : { onClick: () => navigate(-1), label: 'Back' }

  return (
    <div className="space-y-6" ref={formContainerRef}>
      <EditingReturnBanner returnTo={returnTo} contextLabel="Clamp Information" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={
          <>
            <span>Equipment Details</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      <div className="min-w-0 space-y-6 border-0 p-0">
      <div className="grid gap-4 md:grid-cols-2">
        <div data-focus-field="clampType">
          <label className="text-xs font-medium text-gray-500">Line-up Clamp</label>
          <MultiSelect
            values={clampType}
            options={clampOptions}
            placeholder="Select clamp type..."
            addLabel="Add Clamp Type"
            onChange={setClampType}
            onCreateOption={async (value) => {
              await addLabel('equipment_clamp_type', value)
              setClampType((prev) => (prev.includes(value) ? prev : [...prev, value]))
            }}
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Head Count</label>
          <div className="mt-1 rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500">
            <InlineSelect
              value={clampCount}
              fullWidth
              options={[
                { value: '0', label: '0' },
                { value: '2', label: '2' },
                { value: '4', label: '4' },
                { value: '6', label: '6' },
                { value: '8', label: '8' },
                { value: '10', label: '10' }
              ]}
              onChange={setClampCount}
            />
          </div>
        </div>
      </div>

      {passList.length > 0 && (
        <div
          data-focus-field="weldingPowerSupplies"
          className="rounded-lg border border-gray-200 bg-white p-3"
        >
          <p className="text-xs font-semibold text-gray-700">Power supply by pass</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {passList.map((pass) => (
              <div key={pass}>
                <label className="text-xs font-medium text-gray-500">{pass}</label>
                <MultiSelect
                  values={passPowerSupplies[pass] ? [passPowerSupplies[pass]] : []}
                  options={powerSupplyOptions}
                  placeholder="Select power supply..."
                  addLabel="Add Power Supply"
                  onChange={(values) =>
                    setPassPowerSupplies((prev) => ({
                      ...prev,
                      [pass]: values.length ? values[values.length - 1] : ''
                    }))
                  }
                  onCreateOption={async (value) => {
                    await addLabel('equipment_power_supply', value)
                    setPassPowerSupplies((prev) => ({
                      ...prev,
                      [pass]: value
                    }))
                  }}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.from(new Set(Object.values(passPowerSupplies).filter(Boolean))).length > 0 && (
        <div className="space-y-4">
          {Array.from(new Set(Object.values(passPowerSupplies).filter(Boolean))).map((supply) => {
            const details = powerSupplyDetails[supply] || {
              enabled: false,
              firmwareVersions: [],
              programs: [],
              firmwareOptions: [],
              programOptions: []
            }
            return (
              <div key={supply} className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-gray-700">
                    {supply} power supply details
                  </p>
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      checked={details.enabled}
                      onChange={(event) =>
                        setPowerSupplyDetails((prev) => ({
                          ...prev,
                          [supply]: {
                            ...details,
                            enabled: event.target.checked
                          }
                        }))
                      }
                      className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40 disabled:cursor-not-allowed"
                    />
                    Enter details for {supply}
                  </label>
                </div>
                {details.enabled && (
                  <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <div data-focus-field="firmware">
                      <label className="text-xs font-medium text-gray-500">
                        {supply} Firmware Version
                      </label>
                      <MultiSelect
                        values={details.firmwareVersions}
                        options={details.firmwareOptions}
                        placeholder="Select firmware version..."
                        addLabel="Add Firmware Version"
                        onChange={(values) =>
                          setPowerSupplyDetails((prev) => ({
                            ...prev,
                            [supply]: {
                              ...details,
                              firmwareVersions: values
                            }
                          }))
                        }
                        onCreateOption={(value) =>
                          setPowerSupplyDetails((prev) => ({
                            ...prev,
                            [supply]: {
                              ...details,
                              firmwareVersions: details.firmwareVersions.includes(value)
                                ? details.firmwareVersions
                                : [...details.firmwareVersions, value],
                              firmwareOptions: details.firmwareOptions.includes(value)
                                ? details.firmwareOptions
                                : [...details.firmwareOptions, value]
                            }
                          }))
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div
        data-focus-field="program"
        className="rounded-lg border border-gray-200 bg-white p-3"
      >
        <p className="text-xs font-semibold text-gray-700">Fronius Program</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-gray-500">Hot pass</label>
            <input
              className="input-primary mt-1 h-9 w-full text-xs"
              value={froniusProgram.hotPass}
              onChange={(e) =>
                setFroniusProgram((prev) => ({ ...prev, hotPass: e.target.value }))
              }
              placeholder="Hot pass program"
              data-testid="fronius-program-hot-pass"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Fill and Caps</label>
            <input
              className="input-primary mt-1 h-9 w-full text-xs"
              value={froniusProgram.fillAndCaps}
              onChange={(e) =>
                setFroniusProgram((prev) => ({ ...prev, fillAndCaps: e.target.value }))
              }
              placeholder="Fill and Caps program"
              data-testid="fronius-program-fill-caps"
            />
          </div>
        </div>
      </div>

      <div data-focus-field="clampRelease">
        <label className="text-xs font-medium text-gray-500">Removal</label>
        <textarea
          className="input-primary mt-1 h-24"
          value={removalNote}
          onChange={(event) => setRemovalNote(event.target.value)}
          placeholder="Not before 100% completion of the Root Pass"
        />
      </div>
      </div>
    </div>
  )
}
